import { useEffect, useState } from "react";
import { Check, X, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AdminAPI } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/navigation";
import { formatDate } from "@/lib/dummyData";
import { toast } from "sonner";

// Halaman persetujuan berjenjang pengurus wilayah:
//  - Admin Pusat menyetujui pendaftaran Admin Daerah
//  - Admin Daerah menyetujui pendaftaran Admin Cabang (di wilayahnya)
//  - Admin Cabang menyetujui pendaftaran Admin Ranting (di wilayahnya)
// Backend (GET /admin/staff-registrations) sudah memfilter sesuai jenjang & wilayah admin yang login.
export default function StaffApprovals() {
  const [status, setStatus] = useState("PENDING");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setRows(await AdminAPI.staffRegistrations(status)); }
    catch { toast.error("Gagal memuat pengajuan (pastikan Anda berwenang)"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    try { await AdminAPI.approveStaff(id); toast.success("Pengurus disetujui & akun dibuat"); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal menyetujui"); }
  };
  const reject = async (id) => {
    try { await AdminAPI.rejectStaff(id, "Ditolak oleh admin"); toast.success("Pengajuan ditolak"); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Gagal menolak"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Persetujuan Pengurus Wilayah</h1>
        <p className="text-ink/75 mt-2 text-sm">Verifikasi pendaftaran Admin Daerah/Cabang/Ranting secara berjenjang sesuai wilayah Anda.</p>
      </div>

      <div className="flex gap-2">
        {["PENDING", "APPROVED", "REJECTED"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`px-3 py-2 rounded-lg text-xs font-medium ${status === s ? "bg-forest text-white" : "bg-cream text-ink/75 hover:bg-stone-soft/30"}`}>
            {s}
          </button>
        ))}
      </div>

      <Card className="rounded-xl border-stone-soft p-5">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
              <TableHead className="text-[10px] uppercase tracking-wider">Nama</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Peran Diajukan</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Wilayah</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Kontak</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Tanggal</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-ink/55 py-10">Memuat…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-ink/55 py-10">Tidak ada pengajuan.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className="border-stone-soft hover:bg-cream/30" data-testid={`staff-reg-row-${r.id}`}>
                <TableCell className="font-medium text-sm text-ink">{r.name}</TableCell>
                <TableCell><Badge className="bg-forest/10 text-forest border-0 rounded-full text-[10px] uppercase tracking-wider"><ShieldCheck size={11} className="mr-1" />{ROLE_LABELS[r.requested_role] || r.requested_role}</Badge></TableCell>
                <TableCell className="text-sm text-ink/70">{r.org_name}</TableCell>
                <TableCell className="text-sm text-ink/70">{r.phone_number}</TableCell>
                <TableCell className="text-sm text-ink/70">{formatDate(r.created_at)}</TableCell>
                <TableCell className="text-right">
                  {r.status === "PENDING" ? (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" className="rounded-lg bg-forest hover:bg-forest-dark text-white h-8" onClick={() => approve(r.id)} data-testid={`staff-reg-approve-${r.id}`}><Check size={14} /> Setujui</Button>
                      <Button size="sm" variant="outline" className="rounded-lg border-terracotta/40 text-terracotta h-8" onClick={() => reject(r.id)} data-testid={`staff-reg-reject-${r.id}`}><X size={14} /></Button>
                    </div>
                  ) : (
                    <span className="text-xs text-ink/45">{r.status}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
